import React from 'react';
import PropTypes from 'prop-types';
import history from 'State/history';
import Typography from 'Components/UI/Typography';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import Dialog from './Dialog';

function BlockingDialog({
  resource,
  disabled: disabledProp,
  onProceedWithoutSaving,
  onConfirm,
  cancelLabel,
  confirmLabel,
  ...rest
}) {
  const [openState, setOpenState] = React.useState({
    open: false,
    nextAction: null,
    location: null,
  });
  const [disabledState, setDisabled] = React.useState(false);
  const unblockRef = React.useRef(null);
  // The following code is necessary due to the how the URL schema for our app is defined.
  // When we're using the PWA we'll throw in '.../mobile/...' inside of the URL.  Because
  // we're hooking into history with history.block, this can trigger our 'Unsaved Changes' Modal
  // In order to get around this, we subscribe to the 'resize' event on the window and set isResizing
  // to true for the remaining calls on the run-time stack
  const resizingRef = React.useRef({
    isResizing: false,
  });

  const disabled = disabledProp || disabledState;

  React.useEffect(() => {
    const resizeListener = () => {
      resizingRef.current.isResizing = true;
      // eslint-disable-next-line no-void
      void Promise.resolve().then(() => {
        resizingRef.current.isResizing = false;
      });
    };

    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  React.useEffect(() => {
    unblockRef.current = history.block((location, action) => {
      if (resizingRef.current.isResizing) return true; // <= Here's where we allow changes to history to go through after resizing

      if (resource.isDirty()) {
        setOpenState({
          open: true,
          location,
          nextAction: action,
        });
        return false;
      }
      return true;
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [resource.instanceId()]);

  const navigate = () => {
    // eslint-disable-next-line no-unused-expressions
    unblockRef.current?.();
    switch (openState.nextAction) {
      case 'PUSH': {
        history.push(openState.location);
        break;
      }
      case 'POP': {
        history.goBack();
        break;
      }
      default: {
        break;
      }
    }
  };

  const handleCancel = async () => {
    try {
      setDisabled(true);
      await onProceedWithoutSaving();
      navigate();
    } catch {
      setOpenState({});
    } finally {
      setDisabled(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setDisabled(true);
      await onConfirm();
      navigate();
    } catch {
      setOpenState({});
    } finally {
      setDisabled(false);
    }
  };

  return (
    <Dialog
      open={openState.open}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      onClose={() => setOpenState({})}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      ConfirmButtonProps={{
        disabled,
        test: 'proceed-and-save',
      }}
      CancelButtonProps={{
        disabled,
        variant: 'round',
        test: 'proceed-without-saving',
      }}
      {...rest}
    >
      {disabled && <LoadingOverlay />}
      <Typography>
        {'Select save and leave if you\'d like to save before leaving.  Click "X" or esc to return to the page to review.'}
      </Typography>
    </Dialog>
  );
}

BlockingDialog.defaultProps = {
  cancelLabel: 'Disregard & Leave',
  confirmLabel: 'Save and Leave',
  disabled: false,
};

BlockingDialog.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onProceedWithoutSaving: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  disabled: PropTypes.bool,
};

export default BlockingDialog;
